import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Input,
  FormLabel,
  Textarea,
  Flex,
  Text,
  FormErrorMessage,
  useTheme,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { useToastContext } from "../../toastNotificationProvider/ToastNotificationProvider";
import SubmitButton from "../../buttons/SubmitButton";
import { getAllSubjects } from "../../../store/slice/subjectSlice";
import {
  createChapter,
  updateChapter,
} from "../../../store/slice/chapterSlice";
import { getAllChapters } from "../../../store/slice/chapterSlice";
import { useDispatch, useSelector } from "react-redux";

const AddChapterModal = ({ isOpen, onClose, editData }) => {
  const theme = useTheme();
  const { addNotification } = useToastContext();
  const { primaryBlueLight, primaryBlue, placeholderOuterColor } =
    theme.colors.pallete;
  const dispatch = useDispatch();
  const { subjects } = useSelector((state) => state.subject);

  const [formData, setFormData] = useState({
    subjectId: "",
    name: "",
    description: "",
    displayOrder: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setFormData({
          subjectId: editData.subjectId || "",
          name: editData.name || "",
          description: editData.description || "",
          displayOrder: editData.displayOrder || "",
        });
      } else {
        setFormData({
          subjectId: "",
          name: "",
          description: "",
          displayOrder: "",
        });
      }
    }
  }, [isOpen, editData]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        subjectId: "",
        name: "",
        description: "",
        displayOrder: "",
      });
      setErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllSubjects());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllChapters());
    }
  }, [isOpen, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      subjectId: Number(selectedOption.value),
    }));
    setErrors((prev) => ({ ...prev, subjectId: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.subjectId) newErrors.subjectId = "Subject is required.";
    if (!formData.name) newErrors.name = "Name is required.";
    if (!formData.description)
      newErrors.description = "Description is required.";

    const displayOrder = formData.displayOrder?.toString().trim();
    if (!displayOrder) {
      newErrors.displayOrder = "Display Order is required.";
    } else if (isNaN(displayOrder) || Number(displayOrder) <= 0) {
      newErrors.displayOrder = "Display Order must be a  valid number.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      subjectId: formData.subjectId,
      name: formData.name,
      description: formData.description,
      displayOrder: Number(formData.displayOrder),
    };
    setIsSubmitLoading(true);
    if (editData) {
      dispatch(updateChapter({ id: editData.id, formData }))
        .unwrap()
        .then((response) => {
          addNotification("Chapter updated successfully", "success", 3000);
          onClose();
        })
        .catch((error) => {
          addNotification(error, "warning", 3000);
        })
        .finally(() => {
          setIsSubmitLoading(false);
        });
    } else {
      dispatch(createChapter(payload))
        .unwrap()
        .then((response) => {
          addNotification("Chapter created successfully", "success", 3000);

          onClose();
        })
        .catch((error) => {
          addNotification(error, "warning", 4000);
        })
        .finally(() => {
          setIsSubmitLoading(false);
        });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={"18px"} fontWeight={400} textAlign="center">
          {editData ? "Update Chapter" : "Add Chapter"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl isRequired isInvalid={!!errors.subjectId}>
            <FormLabel>Subject</FormLabel>
            <Select
              fontSize="14px"
              borderColor={placeholderOuterColor}
              placeholder="Select Subject"
              value={
                formData.subjectId
                  ? {
                      value: formData.subjectId.toString(),
                      label:
                        subjects.find((s) => s.id === formData.subjectId)
                          .name ?? "",
                    }
                  : null
              }
              onChange={handleSelectChange}
              options={subjects.map((subject) => ({
                value: subject.id.toString(),
                label: subject.name,
              }))}
              isReadOnly={!!editData}
              useBasicStyles
            />
            <FormErrorMessage>{errors.subjectId}</FormErrorMessage>
            {editData && (
              <Text fontSize="12px" color="gray.500" mt={1}>
                Subject name cannot be edited after creation.
              </Text>
            )}
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              name="name"
              fontSize={"14px"}
              borderColor={"gray.300"}
              placeholder="Enter chapter name"
              value={formData.name}
              onChange={handleChange}
              isReadOnly={!!editData}
              useBasicStyles
            />
            <FormErrorMessage>{errors.name}</FormErrorMessage>
            {editData && (
              <Text fontSize="12px" color="gray.500" mt={1}>
                Chapter name cannot be edited after creation.
              </Text>
            )}
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!errors.description}>
            <FormLabel>Description</FormLabel>
            <Textarea
              name="description"
              borderColor={"gray.300"}
              fontSize={"14px"}
              placeholder="Enter description"
              value={formData.description}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.description}</FormErrorMessage>
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!errors.displayOrder}>
            <FormLabel>Display Order</FormLabel>
            <Input
              name="displayOrder"
              fontSize={"14px"}
              borderColor={"gray.300"}
              placeholder="Enter display order"
              value={formData.displayOrder}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.displayOrder}</FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Flex w={"full"} justifyContent={"center"}>
            <SubmitButton
              text={editData ? "Update" : "Submit"}
              backColor={primaryBlue}
              textColor="white"
              hoverColor={primaryBlueLight}
              onClickHandler={handleSubmit}
              isLoading={isSubmitLoading}
            />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddChapterModal;
